import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  Box,
  Button,
  useToast,
  HStack,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Image,
  Checkbox,
  Spinner,
} from "@chakra-ui/react";
import { Plus, Utensils, Bookmark, Search, Check } from "lucide-react";
import { useGetLists, useCreateAndAddToList, useAddRestaurantWithId, useGetRestaurantLists } from "@/api";
import { useRemoveRestaurantFromList } from "@/api/mutations/useListsMutations";
import { Restaurant } from "@/types/restaurant";
import { AddToListModalSkeleton } from "@/components/skeletons";

interface AddToListModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurant: Restaurant;
}

export function AddToListModal({
  isOpen,
  onClose,
  restaurant
}: AddToListModalProps) {
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Optimistic local set — updated immediately on toggle so the UI feels instant
  const [localListKeys, setLocalListKeys] = useState<Set<string>>(new Set());
  // Track which list URLs have a pending mutation so we can show a spinner
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  const toast = useToast();

  const { data: listsData, isLoading, isError, refetch: refetchLists } = useGetLists({
    enabled: isOpen
  });
  const { data: restaurantListsData, isLoading: isLoadingRestaurantLists, refetch: refetchRestaurantLists } = useGetRestaurantLists(
    restaurant.id,
    { enabled: isOpen }
  );

  // Sync local state from server whenever the server data changes
  useEffect(() => {
    setLocalListKeys(new Set(restaurantListsData?.ownedLists?.map(l => l.urlKey) || []));
  }, [restaurantListsData]);

  // Reset local state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowCreateNew(false);
      setNewListName("");
      setSearchQuery("");
    }
  }, [isOpen]);

  const { mutateAsync: createAndAddRestaurantToList, isPending: isCreating } = useCreateAndAddToList();
  const { mutateAsync: addRestaurantToList } = useAddRestaurantWithId();
  const { mutateAsync: removeRestaurantFromList } = useRemoveRestaurantFromList();

  const ownedLists = listsData?.ownedLists || [];

  const filteredLists = useMemo(() => {
    if (!searchQuery.trim()) return ownedLists;
    const query = searchQuery.toLowerCase();
    return ownedLists.filter(list => list.name.toLowerCase().includes(query));
  }, [ownedLists, searchQuery]);

  const handleToggleList = async (urlKey: string) => {
    if (pendingKeys.has(urlKey)) return;

    const isInList = localListKeys.has(urlKey);

    // Optimistic update
    setLocalListKeys(prev => {
      const next = new Set(prev);
      if (isInList) next.delete(urlKey); else next.add(urlKey);
      return next;
    });
    setPendingKeys(prev => new Set(prev).add(urlKey));

    try {
      if (isInList) {
        await removeRestaurantFromList({ urlKey, restaurantId: restaurant.id });
      } else {
        await addRestaurantToList({ urlKey, restaurantId: restaurant.id });
      }
      refetchRestaurantLists();
      refetchLists();
    } catch (error) {
      // Revert optimistic update on failure
      setLocalListKeys(prev => {
        const next = new Set(prev);
        if (isInList) next.add(urlKey); else next.delete(urlKey);
        return next;
      });
      toast({
        title: isInList ? "Failed to remove from list" : "Failed to add to list",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setPendingKeys(prev => {
        const next = new Set(prev);
        next.delete(urlKey);
        return next;
      });
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newListName.trim() || !restaurant) return;

    try {
      const result = await createAndAddRestaurantToList({
        listName: newListName.trim(),
        restaurantId: restaurant.id,
      });

      // Optimistically mark the new list as containing this restaurant
      setLocalListKeys(prev => new Set(prev).add(result.urlKey));

      setNewListName("");
      setShowCreateNew(false);
      refetchRestaurantLists();
      refetchLists();
    } catch (error) {
      toast({
        title: "Failed to create list",
        description: "Please try again later.",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    }
  };

  const renderContent = () => {
    if (isLoading || isLoadingRestaurantLists) {
      return (
        <Box minH="200px">
          <AddToListModalSkeleton count={4} />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box textAlign="center" py={8}>
          <Text color="red.400">Failed to load your lists</Text>
        </Box>
      );
    }

    if (ownedLists.length === 0 || showCreateNew) {
      return (
        <VStack spacing={4}>
          {ownedLists.length === 0 && !showCreateNew && (
            <Text fontSize="lg" textAlign="center" color="var(--card-text-3)" pt={4}>
              You don't have any lists yet
            </Text>
          )}
          <Text fontSize="lg" fontWeight="bold">Create New List</Text>
          <Box w="100%">
            <input
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="Enter list name..."
              autoFocus
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "var(--r-chip)",
                border: "2px solid var(--color-gray-200)",
                fontSize: "16px"
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateAndAdd()}
            />
          </Box>
          <HStack spacing={4} w="100%">
            {ownedLists.length > 0 && (
              <Button
                flex={1}
                variant="outline"
                onClick={() => { setShowCreateNew(false); setNewListName(""); }}
              >
                Cancel
              </Button>
            )}
            <Button
              flex={1}
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={handleCreateAndAdd}
              isLoading={isCreating}
              isDisabled={!newListName.trim()}
            >
              Create & Add
            </Button>
          </HStack>
        </VStack>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        {/* Search bar */}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Search size={18} color="var(--chakra-colors-gray-400)" />
          </InputLeftElement>
          <Input
            placeholder="Search your lists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="var(--card-surface-2)"
            border="1px solid"
            borderColor="var(--card-divider)"
            _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
            _placeholder={{ color: "var(--card-text-3)" }}
          />
        </InputGroup>

        {/* Create new button */}
        <Button
          w="100%"
          variant="outline"
          color="purple.500"
          borderColor="purple.300"
          borderStyle="dashed"
          _hover={{ bg: 'purple.50', borderColor: 'purple.500' }}
          onClick={() => setShowCreateNew(true)}
          leftIcon={<Plus size={18} />}
          py={6}
        >
          Create New List
        </Button>

        {/* Lists */}
        <Box maxH="300px" overflowY="auto" mx={-2} px={2}>
          <VStack spacing={2} align="stretch">
            {filteredLists.length === 0 && searchQuery && (
              <Text color="var(--card-text-3)" textAlign="center" py={4}>
                No lists match "{searchQuery}"
              </Text>
            )}

            {filteredLists.map((list) => {
              const isChecked = localListKeys.has(list.urlKey);
              const isPending = pendingKeys.has(list.urlKey);

              return (
                <HStack
                  key={list.urlKey}
                  p={3}
                  borderWidth="1px"
                  borderColor={isChecked ? "purple.300" : "var(--card-divider)"}
                  borderRadius="var(--r-inner)"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ borderColor: "purple.400", bg: "purple.50" }}
                  onClick={() => handleToggleList(list.urlKey)}
                  bg={isChecked ? "purple.50" : "var(--card-bg)"}
                  justify="space-between"
                  aria-label={
                    isChecked
                      ? `${list.name} — already added`
                      : list.name
                  }
                >
                  <HStack spacing={3} flex={1} minW={0}>
                    {/* List thumbnail — shows coverArt when available, falls back to icon */}
                    <Box
                      position="relative"
                      w="36px"
                      h="36px"
                      borderRadius="var(--r-chip)"
                      bg={isChecked ? "purple.100" : "var(--card-surface-2)"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      {list.coverArt ? (
                        <Image
                          src={list.coverArt}
                          alt={list.name}
                          w="36px"
                          h="36px"
                          borderRadius="md"
                          objectFit="cover"
                        />
                      ) : (
                        <Box
                          w="36px"
                          h="36px"
                          borderRadius="md"
                          bg={isChecked ? "green.100" : "gray.100"}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Utensils size={16} color="var(--chakra-colors-gray-500)" />
                        </Box>
                      )}
                      {/* Green check badge overlay — mirrors the row checkbox for at-a-glance membership */}
                      {isChecked && (
                        <Box
                          position="absolute"
                          bottom={0}
                          right={0}
                          w="14px"
                          h="14px"
                          borderRadius="full"
                          bg="green.500"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          border="1.5px solid white"
                        >
                          <Check size={8} color="white" />
                        </Box>
                      )}
                    </Box>

                    <VStack align="start" spacing={0} flex={1} minW={0}>
                      <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                        {list.name}
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="var(--card-text-3)">
                          {list.restaurantCount || 0} {list.restaurantCount === 1 ? 'place' : 'places'}
                        </Text>
                        {list.isPublic && (
                          <Badge size="sm" colorScheme="purple" fontSize="xs">
                            Public
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>

                  {isPending ? (
                    <Spinner size="sm" color="purple.500" flexShrink={0} />
                  ) : (
                    <Checkbox
                      isChecked={isChecked}
                      colorScheme="purple"
                      pointerEvents="none"
                      flexShrink={0}
                    />
                  )}
                </HStack>
              );
            })}
          </VStack>
        </Box>
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="teal.100" borderRadius="var(--r-inner)" p={2}>
              <Bookmark size={20} color="var(--chakra-colors-teal-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Save to List
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal" noOfLines={1}>
                {restaurant?.name}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {renderContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

